import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  rem,
  SegmentedControl,
  useMantineTheme,
} from "@mantine/core";
import "./Calendar.scss";
import { useMediaQuery } from "@mantine/hooks";

import dayjs from "dayjs";
import "dayjs/locale/uk";
import "dayjs/locale/en";
import minMax from "dayjs/plugin/minMax";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isBetween from "dayjs/plugin/isBetween";
import { useTranslation } from "react-i18next";

import useDevelopmentStore from "../../store/useDevelopmentStore";
import useProfileStore from "../../store/useProfileStore";
import useCoursesStore from "../../store/useCoursesStore";

import { Goal, GoalTimestamp } from "../../types/api";
import {
  CalendarEditTimestamp,
  TaskFormValues,
} from "../../features/calendar/calendar-timestamps/CalendarEditTimestamp";
import { CalendarAddTimestamp } from "../../features/calendar/calendar-timestamps/CalendarAddTimestamp";
import EditGoalModal from "../../features/calendar/calendar-goal/EditGoalModal";
import GoalModal from "../../features/calendar/calendar-goal/AddGoalModal";
import { CalendarSelector } from "../../features/calendar/calendar/CalendarSelect";

import CalendarMonthView from "./CalendarMonthView";
import CalendarYearView from "./CalendarYearView";
import CalendarWeekView from "./CalendarWeekView";
import { useLocation, useNavigate } from "react-router";
import {
  OnboardingTourFocusRevealProps,
  OnboardingTourStep,
} from "@gfazioli/mantine-onboarding-tour";
import { scrollToTop } from "../../utils/helpers";
import { useOnboardingLearningPlanSteps } from "../../utils/onboardingTourSteps";
import { ReusableOnboardingTour } from "../../utils/components/onboardingTour/OnboardingTourWrapper";
import { TourButton } from "../../utils/components/onboardingTour/TourButton";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(minMax);

export function normalizeGoalTimestamp(
  task: GoalTimestamp
): TaskFormValues | null {
  if (
    !task.id ||
    !task.title ||
    !task.day_from ||
    !task.day_to ||
    !task.display_color
  ) {
    return null;
  }

  return {
    id: task.id,
    title: task.title,
    description: task.description ?? "",
    completed: task.completed ?? false,
    reminder_me: task.reminder_me ?? false,
    day_from: task.day_from,
    day_to: task.day_to,
    display_color: task.display_color,
  };
}
type ViewMode = "month" | "week" | "year";

export const Calendar = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const {
    fetchDevelopmentPlan,
    goals,
    fetchLearningPlanGoals,
    currentPlanId,
    learningPlan,
  } = useDevelopmentStore();

  const { profileLanguage, profile, tourButtonShow, fetchProfile } =
    useProfileStore();
  const { fetchLearningCourses } = useCoursesStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [editGoal, setEditGoal] = useState<Goal | null>(null);
  const [editTask, setEditTask] = useState<GoalTimestamp | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [daysInMonth, setDaysInMonth] = useState<Array<number>>([]);
  const [currentWeek, setCurrentWeek] = useState(dayjs());

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const [isTaskOpen, setIsTaskOpen] = useState(false);

  const taskRowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rowHeights, setRowHeights] = useState<number[]>([]);

  const theme = useMantineTheme();
  const isMobile = useMediaQuery("(pointer: coarse)");
  const matches = useMediaQuery("(max-width: 650px)");

  const { t } = useTranslation();

  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (
      profile?.display_preferences?.hide_tour_tooltip_on_dev_plan !== null &&
      profile?.display_preferences?.hide_tour_tooltip_on_dev_plan !== undefined
    )
      setStarted(!profile?.display_preferences?.hide_tour_tooltip_on_dev_plan);
  }, [profile?.display_preferences?.hide_tour_tooltip_on_dev_plan]);

  useEffect(() => {
    if (!profile) fetchProfile();
  }, []);

  const focusRevealProps: OnboardingTourFocusRevealProps = {
    popoverProps: {
      arrowSize: 20,
      position: matches ? "bottom" : "left",
    },
  };

  const steps = useOnboardingLearningPlanSteps();

  const [filteredSteps, setFilteredSteps] = useState<OnboardingTourStep[]>([]);

  const title = learningPlan?.find((el) => el.id === currentPlanId)?.title;

  const filterExistingSteps = (steps: OnboardingTourStep[]) => {
    return steps.filter((step) => document.getElementById(step.id));
  };

  useEffect(() => {
    const stepsToShow = filterExistingSteps(
      title === "Default Learning Plan"
        ? steps.filter((i) => i.id !== "edit-calendar")
        : steps
    );

    setFilteredSteps(stepsToShow);
  }, [title, viewMode, goals, profileLanguage]);

  const commonStyles: React.CSSProperties = {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontSize: rem(14),
    padding: `${rem(4)} ${rem(8)}`,
    borderRadius: rem(4),
    boxShadow: theme.shadows.sm,
    maxWidth: isMobile ? rem(300) : rem(400),
    wordWrap: "break-word" as "break-word",
    whiteSpace: "normal",
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const goalIdFromUrl = Number(params.get("goalId"));

    const fn = async () => {
      if (goalIdFromUrl !== 0) {
        const goalToEdit = goals.find((i) => i.id === goalIdFromUrl);

        if (goalToEdit) {
          setEditGoal(goalToEdit);
          setIsEditOpen(true);
        }
      }
    };
    fn();
  }, [location.search, fetchLearningPlanGoals]);

  useEffect(() => {
    const newHeights = taskRowRefs.current.map((el) => el?.offsetHeight || 58);
    setRowHeights(newHeights);
  }, [goals, currentMonth, viewMode]);

  useEffect(() => {
    fetchDevelopmentPlan();
  }, []);

  useEffect(() => {
    dayjs.locale(Object.keys(profileLanguage!)[0]);
    setCurrentMonth(dayjs());
    setCurrentWeek(dayjs());
  }, [profileLanguage]);

  useEffect(() => {
    fetchLearningCourses();
  }, []);

  useEffect(() => {
    if (learningPlan.length > 0 && currentPlanId) {
      fetchLearningPlanGoals(currentPlanId);
    }
  }, [currentPlanId]);

  useEffect(() => {
    const numberOfDays = currentMonth.daysInMonth();
    setDaysInMonth(Array.from({ length: numberOfDays }, (_, i) => i + 1));
  }, [currentMonth, profileLanguage]);

  const changeMonth = (direction: "next" | "prev") => {
    setCurrentMonth((prevMonth) =>
      direction === "next"
        ? prevMonth.add(1, "month")
        : prevMonth.subtract(1, "month")
    );
  };

  const changeWeek = (direction: "prev" | "next") => {
    setCurrentWeek((prevWeek) =>
      direction === "next"
        ? prevWeek.add(1, "week")
        : prevWeek.subtract(1, "week")
    );
  };
  const changeYear = (direction: "next" | "prev") => {
    setCurrentMonth((prevMonth) =>
      direction === "next"
        ? prevMonth.add(1, "year")
        : prevMonth.subtract(1, "year")
    );
  };

  return (
    <>
      <ReusableOnboardingTour
        focusRevealProps={focusRevealProps}
        tour={filteredSteps}
        started={started}
        onOnboardingTourEnd={() => {
          tourButtonShow({ hide_tour_tooltip_on_dev_plan: true });
          scrollToTop();
        }}
        maw={matches ? 330 : 270}
        __size="xs"
      >
        <Flex
          align={matches ? "start" : "end"}
          justify="space-between"
          direction={matches ? "column" : "row"}
          mb={32}
          gap={20}
          id="calendar-view"
        >
          <Box w={matches ? "100%" : "50%"}>
            <CalendarSelector />
          </Box>
          <SegmentedControl
            data-onboarding-tour-id="calendar-view"
            value={viewMode}
            w={matches ? "100%" : "auto"}
            onChange={(value) => setViewMode(value as ViewMode)}
            data={[
              { label: t("week"), value: "week" },
              { label: t("month"), value: "month" },
              { label: t("year"), value: "year" },
            ]}
          />
        </Flex>

        {viewMode === "month" && (
          <CalendarMonthView
            currentMonth={currentMonth}
            changeMonth={changeMonth}
            daysInMonth={daysInMonth}
            goals={goals}
            rowHeights={rowHeights}
            taskRowRefs={taskRowRefs}
            commonStyles={commonStyles}
            setIsOpen={setIsOpen}
            setIsEditOpen={setIsEditOpen}
            setEditGoal={setEditGoal}
            setIsTaskOpen={setIsTaskOpen}
            setSelectedDate={setSelectedDate}
            setIsEditTaskOpen={setIsEditTaskOpen}
            setEditTask={setEditTask}
          />
        )}

        {viewMode === "week" && (
          <CalendarWeekView
            currentWeek={currentWeek}
            changeWeek={changeWeek}
            goals={goals}
            rowHeights={rowHeights}
            taskRowRefs={taskRowRefs}
            commonStyles={commonStyles}
            setIsOpen={setIsOpen}
            setIsEditOpen={setIsEditOpen}
            setEditGoal={setEditGoal}
            setIsTaskOpen={setIsTaskOpen}
            setSelectedDate={setSelectedDate}
            setIsEditTaskOpen={setIsEditTaskOpen}
            setEditTask={setEditTask}
          />
        )}

        {viewMode === "year" && (
          <CalendarYearView
            currentYear={currentMonth}
            changeYear={changeYear}
            daysInMonth={daysInMonth}
            goals={goals}
            rowHeights={rowHeights}
            taskRowRefs={taskRowRefs}
            commonStyles={commonStyles}
            setIsOpen={setIsOpen}
            setIsEditOpen={setIsEditOpen}
            setEditGoal={setEditGoal}
            setIsTaskOpen={setIsTaskOpen}
            setSelectedDate={setSelectedDate}
            setIsEditTaskOpen={setIsEditTaskOpen}
            setEditTask={setEditTask}
          />
        )}
      </ReusableOnboardingTour>
      <GoalModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        setIsEditOpen={setIsEditOpen}
        setEditGoal={setEditGoal}
      />
      <EditGoalModal
        isOpen={isEditOpen}
        setIsOpen={(value) => {
          setIsEditOpen(value);
          if (!value) {
            setSelectedDate(null);
            setEditGoal(null);
            navigate("/development-plan");
          }
        }}
        goal={editGoal}
      />
      <CalendarAddTimestamp
        opened={isTaskOpen}
        selectedDate={selectedDate}
        onClose={() => {
          setIsTaskOpen(false);
          setSelectedDate(null);
        }}
        goalId={editGoal?.id || null}
      />
      <CalendarEditTimestamp
        opened={isEditTaskOpen}
        taskToEdit={editTask ? normalizeGoalTimestamp(editTask) : null}
        onClose={() => setIsEditTaskOpen(false)}
        goalId={editGoal?.id || null}
      />

      <TourButton
        onClick={() => {
          tourButtonShow({ hide_tour_tooltip_on_dev_plan: false });
        }}
      />
    </>
  );
};
